"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useDecisionPoints } from "@/hooks/waves/useDecisionPoints";
import { AnimatePresence } from "framer-motion";
import { TimelineToggleHeader } from "./time/TimelineToggleHeader";
import { ExpandedTimelineContent } from "./time/ExpandedTimelineContent";
import { CompactDroppingPhaseCard } from "./time/CompactDroppingPhaseCard";
import { CompactVotingPhaseCard } from "./time/CompactVotingPhaseCard";
import { useWave } from "@/hooks/useWave";
import { Time } from "@/helpers/time";
interface WaveLeaderboardTimeProps {
  readonly wave: ApiWave;
}
const AUTO_EXPAND_LIMIT = 5;
export const WaveLeaderboardTime: React.FC<WaveLeaderboardTimeProps> = t0 => {
  const $ = _c(41);
  const {
    wave
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      initialPastWindow: 4,
      initialFutureWindow: 20
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    allDecisions,
    hasMorePast,
    hasMoreFuture,
    loadMorePast,
    loadMoreFuture,
    remainingPastCount,
    remainingFutureCount
  } = useDecisionPoints(wave, t1);
  const {
    decisions: t2,
    isCurationWave,
    pauses: t3
  } = useWave(wave);
  const {
    multiDecision
  } = t2;
  const {
    showPause,
    filterDecisionsDuringPauses
  } = t3;
  const [isDecisionDetailsOpen, setIsDecisionDetailsOpen] = useState(false);
  const autoExpandFutureAttemptsRef = useRef(0);
  const [timelineFocus, setTimelineFocus] = useState(null);
  let t4;
  if ($[1] !== allDecisions || $[2] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t4 = new Set(filtered.map(_temp2));
    $[1] = allDecisions;
    $[2] = filterDecisionsDuringPauses;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const allowedDecisionTimes = t4;
  let t5;
  if ($[4] !== allDecisions || $[5] !== allowedDecisionTimes) {
    let t6;
    if ($[7] !== allowedDecisionTimes) {
      t6 = decision_0 => allowedDecisionTimes.has(decision_0.timestamp);
      $[7] = allowedDecisionTimes;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    t5 = allDecisions.filter(t6);
    $[4] = allDecisions;
    $[5] = allowedDecisionTimes;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const filteredDecisions = t5;
  let t6;
  if ($[9] !== filteredDecisions) {
    t6 = filteredDecisions.find(_temp3)?.timestamp ?? null;
    $[9] = filteredDecisions;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const nextDecisionTime = t6;
  let t7;
  let t8;
  if ($[11] !== hasMoreFuture || $[12] !== loadMoreFuture || $[13] !== nextDecisionTime) {
    t7 = () => {
      if (nextDecisionTime !== null) {
        autoExpandFutureAttemptsRef.current = 0;
        return;
      }
      if (!hasMoreFuture) {
        autoExpandFutureAttemptsRef.current = 0;
        return;
      }
      if (autoExpandFutureAttemptsRef.current >= AUTO_EXPAND_LIMIT) {
        return;
      }
      let timeoutId = null;
      let isCancelled = false;
      const tick = () => {
        if (isCancelled) {
          return;
        }
        if (autoExpandFutureAttemptsRef.current >= AUTO_EXPAND_LIMIT) {
          return;
        }
        autoExpandFutureAttemptsRef.current = autoExpandFutureAttemptsRef.current + 1;
        loadMoreFuture();
        timeoutId = globalThis.setTimeout(tick, 50);
      };
      timeoutId = globalThis.setTimeout(tick, 50);
      return () => {
        isCancelled = true;
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
      };
    };
    t8 = [nextDecisionTime, hasMoreFuture, loadMoreFuture];
    $[11] = hasMoreFuture;
    $[12] = loadMoreFuture;
    $[13] = nextDecisionTime;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useEffect(t7, t8);
  let t9;
  if ($[16] !== hasMorePast || $[17] !== loadMorePast) {
    t9 = () => {
      if (hasMorePast) {
        setTimelineFocus("start");
        loadMorePast();
      }
    };
    $[16] = hasMorePast;
    $[17] = loadMorePast;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleLoadMorePast = t9;
  let t10;
  if ($[19] !== hasMoreFuture || $[20] !== loadMoreFuture) {
    t10 = () => {
      if (hasMoreFuture) {
        setTimelineFocus("end");
        loadMoreFuture();
      }
    };
    $[19] = hasMoreFuture;
    $[20] = loadMoreFuture;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const handleLoadMoreFuture = t10;
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setTimelineFocus(null);
    };
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const onTimelineFocusHandled = t11;
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = isOpen => {
      setIsDecisionDetailsOpen(isOpen);
      if (!isOpen) {
        setTimelineFocus(null);
      }
    };
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const handleDecisionDetailsOpenChange = t12;
  let t13;
  if ($[24] !== filteredDecisions || $[25] !== handleLoadMoreFuture || $[26] !== handleLoadMorePast || $[27] !== hasMoreFuture || $[28] !== hasMorePast || $[29] !== isCurationWave || $[30] !== isDecisionDetailsOpen || $[31] !== multiDecision || $[32] !== nextDecisionTime || $[33] !== remainingFutureCount || $[34] !== remainingPastCount || $[35] !== showPause || $[36] !== timelineFocus || $[37] !== wave) {
    t13 = multiDecision ? <div className="tw-mt-2 tw-overflow-hidden tw-rounded-lg tw-bg-iron-950 md:tw-mt-4">{(() => {
        const currentPause = showPause(nextDecisionTime);
        return <TimelineToggleHeader isOpen={isDecisionDetailsOpen} setIsOpen={handleDecisionDetailsOpenChange} nextDecisionTime={nextDecisionTime} isPaused={Boolean(currentPause)} currentPause={currentPause} wave={wave} />;
      })()}<AnimatePresence>{isDecisionDetailsOpen && <ExpandedTimelineContent decisions={filteredDecisions} nextDecisionTime={nextDecisionTime} onLoadMorePast={hasMorePast ? handleLoadMorePast : undefined} onLoadMoreFuture={hasMoreFuture ? handleLoadMoreFuture : undefined} hasMorePast={hasMorePast} hasMoreFuture={hasMoreFuture} remainingPastCount={remainingPastCount} remainingFutureCount={remainingFutureCount} focus={timelineFocus} onFocusHandled={onTimelineFocusHandled} />}</AnimatePresence></div> : !isCurationWave && <div className="tw-overflow-hidden tw-rounded-lg tw-bg-iron-950 tw-px-3 tw-py-2"><div className="tw-flex tw-items-center tw-gap-2"><CompactDroppingPhaseCard wave={wave} /><CompactVotingPhaseCard wave={wave} /></div></div>;
    $[24] = filteredDecisions;
    $[25] = handleLoadMoreFuture;
    $[26] = handleLoadMorePast;
    $[27] = hasMoreFuture;
    $[28] = hasMorePast;
    $[29] = isCurationWave;
    $[30] = isDecisionDetailsOpen;
    $[31] = multiDecision;
    $[32] = nextDecisionTime;
    $[33] = remainingFutureCount;
    $[34] = remainingPastCount;
    $[35] = showPause;
    $[36] = timelineFocus;
    $[37] = wave;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t13) {
    t14 = <div>{t13}</div>;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  return t14;
};
function _temp(decision) {
  return {
    decision_time: decision.timestamp
  };
}
function _temp2(filteredDecision) {
  return filteredDecision.decision_time;
}
function _temp3(decision_1) {
  return decision_1.timestamp > Time.currentMillis();
}